import React from 'react';

import { Field } from '@opendoor/atoms/field';
import { Text } from '@opendoor/atoms/text';

import { PASSWORD_SUPPORT_TEXT } from './passwordRules';
import { PasswordInput } from './primitives/PasswordInput';

/**
 * The form field name this binds. A host keys `passwordSchema` off it —
 * `z.object({ ...yourFields, [PASSWORD_FIELD_NAME]: passwordSchema })` — so the
 * field and the rule that validates it cannot drift: named apart, the input gets
 * no validation and the rule gets no error slot to render into.
 */
export const PASSWORD_FIELD_NAME = 'password';

export interface NewPasswordFieldProps {
  /** Analytics name for the input; the visibility toggle derives its own from it. */
  analyticsName: string;
  placeholder?: string;
  /**
   * Support copy below the input, which yields to the error message rather than
   * stacking with it. Defaults to the shared length rule's own sentence; pass
   * `null` for a surface that states the rule somewhere else.
   */
  supportText?: React.ReactNode;
}

/**
 * The connected password field for every surface where a user *sets* a password.
 * Atoms has no `Form.PasswordInput`, so this wires `PasswordInput` into a
 * connected `Field` by name — the same composition `Form.Input` is, one control
 * down.
 *
 * `Field` rather than a hand-rolled label plus message, because the chrome it
 * owns is the chrome the sibling `Form.Input`s get: the screen-reader-only
 * "(required)" marker on the label, and the `aria-invalid`/`aria-describedby`
 * that Base UI's `Field.Root` propagates to the control it wraps. Atoms' `Input`
 * prop doc is explicit that `error` is styling only and backfills neither, so a
 * bespoke label here is a field that announces as valid while looking invalid.
 *
 * The support text is a child rather than Field's `description` so it stays
 * below the input, where the Figma puts it, and yields to the error message
 * instead of stacking with it.
 *
 * The message always renders inline, and there is no mode that delegates it to a
 * form-level region: `aria-describedby` resolves only to a node inside this
 * field's `Field.Root`, so a delegating field could not describe its control
 * without hand-managing ids on a shared, multi-message `role="alert"` — the
 * hand-rolling this component exists to delete. A surface that wants its
 * messages only in that region keeps its own field.
 */
export const NewPasswordField: React.FC<NewPasswordFieldProps> = ({
  analyticsName,
  placeholder,
  supportText = PASSWORD_SUPPORT_TEXT,
}) => (
  <Field name={PASSWORD_FIELD_NAME} label="Password" required>
    {(f) => (
      <>
        <PasswordInput
          id={f.id}
          name={f.name}
          value={typeof f.value === 'string' ? f.value : ''}
          // eslint-disable-next-line react-perf/jsx-no-new-function-as-prop -- PasswordInput isn't memoized, so the fresh handler identity is harmless (Field hands down a fresh setValue each render anyway).
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => f.setValue(e.target.value)}
          onBlur={f.onBlur}
          error={f.hasError}
          placeholder={placeholder}
          autoComplete="new-password"
          analyticsName={analyticsName}
        />
        {supportText != null && !f.hasError ? (
          <Text as="p" className="text-body-sm text-od-content-secondary">
            {supportText}
          </Text>
        ) : null}
      </>
    )}
  </Field>
);
